
import Vue, { PropType } from 'vue'
import { Project } from '@/model/Project'
import {SimpleUser, User} from '@/model/User'
import ImageGallery from '@/components/ImageGallery.vue'
import ReportAbuseDialog from '@/components/project/ReportAbuseDialog.vue'
import SubmenuPage from '@/components/SubmenuPage.vue'
import Score from '@/components/Score.vue'
import shareMixin from "@/service/Share";
import sanitizeHtml from 'sanitize-html'
import ContestList from '@/components/project/ContestList.vue'
import {ContestApplication} from "@/model/ContestApplication";

export default Vue.extend({
  name: 'ProjectDetails',
  mixins: [shareMixin],
  props: {
    project: {
      type: Object as PropType<Project>,
    },
    applications: {
      type: Array as PropType<ContestApplication[]>
    },
    preview: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    ImageGallery,
    ReportAbuseDialog,
    SubmenuPage,
    Score,
    ContestList,
  },
  computed: {
    authorName: function (): string {
      const fullName = `${(this as any).project.author.firstName} ${(this as any).project.author.lastName}`;
      const deletedUserPhrase = (this as any).$options.filters.deletedUser((this as any).project.author.nickname);

      return this.authorDeleted
        ? deletedUserPhrase
        : fullName;
    },
    authorDeleted: function (): boolean {
      return (this as any).project.author.isDeleted;
    },
    canEdit: function (): boolean {
      if (!this.$store.state.authentication.signedIn) {
        return false
      }

      if (
        (this.project.author as SimpleUser).nickname !==
        this.$store.state.authentication.user.nickname
      ) {
        return false
      }

      return !this.preview
    },

    canonicalLink: function (): string {
      return (
        'https://' +
        window.location.hostname +
        '/project/' +
        this.project['slug']
      )
    },
  },

  data: () => ({
    shareMenu: ['Facebook'],
    ratingDialog: false,
    reportAbuseDialog: false,
    sendReportLoading: false,
    scoreStoring: false,
  }),

  methods: {
    fetchProject: function () {
      const submenuPage = this.$refs.submenuPage as any

      if (!submenuPage) {
        return
      }

      submenuPage.fetchDescription()
    },

    storeUserScore: function (newScore: number) {
      if (!this.$store.state.authentication.signedIn) {
        this.$store.dispatch(
          'alert/info',
          'Register new account or sign in order to rate projects.'
        )

        return
      }

      this.scoreStoring = true

      this.$api
        .post('/scores/projects/' + this.project.id, {
          score: newScore,
        })
        .then((response: any) => {
          this.$emit('updateUserScore', response.data.score)
          this.$emit('updateUserAverageScore', response.data.average)
          this.$store.dispatch(
            'alert/success',
            'Your project rate has been updated.'
          )
          this.ratingDialog = false
        })
        .catch(() => {
          this.$store.dispatch(
            'alert/error',
            'Problems with updating project rate.'
          )
        })
        .finally(() => {
          this.scoreStoring = false
        })
    },

    reportAbuse: function (description: string) {
      this.sendReportLoading = true
      this.$api
        .post('/report-projects', {
          projectId: this.project.id,
          description: description,
        })
        .then(() => {
          this.$store.dispatch(
            'alert/success',
            'Thanks for reporting the project.'
          )
          this.reportAbuseDialog = false
        })
        .catch(() =>
          this.$store.dispatch(
            'alert/error',
            'Problems with reporting the project.'
          )
        )
        .finally(() => (this.sendReportLoading = false))
    },

    bookmarkProject: function () {
      if (!this.$store.state.authentication.signedIn) {
        this.$store.dispatch(
          'alert/info',
          'Register new account or sign in order to bookmark projects.'
        )

        return
      }

      this.$api
        .post('/likes/projects/' + this.project.id)
        .then((response: any) => {
          this.project.isFavourite = response.data.isLiked
          const message = response.data.isLiked
            ? 'Project has been added to your favourite projects.'
            : 'Project has been removed from your favourite projects.'
          this.$store.dispatch('alert/success', message)
        })
    },

    sanitizeContent(content: string|null) {
      if (!content) return '';

      return sanitizeHtml(content, {
        ...sanitizeHtml.defaults,
        allowedTags: [
          ...sanitizeHtml.defaults.allowedTags,
          'img',
          'iframe',
        ],
        allowedAttributes: {
          ...sanitizeHtml.defaults.allowedAttributes,
          '*': ['style', 'alt', 'class'],
          'iframe': ['src', 'allow', 'allowfullscreen'],
        },
        allowedSchemesByTag: {
          img: ['http', 'https', 'data'],
        },
        allowedStyles: {
          '*': {
            'text-align': [/^left$/i, /^center$/i, /^right$/i, /^justify$/i],
            'font-weight': [/^.*/i],
            'list-style-type': [/^.*/i],
          },
          'img': {
            'width': [/^.*/i],
            'display': [/^.*/i],
            'margin-left': [/^.*/i],
            'margin-right': [/^.*/i],
            'float': [/^.*/i],
          },
        },
      });
    }
  },
})
